import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Phone, Mail, Globe2, CheckCircle } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactSection = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try { await axios.post(`${API}/contact`, form); setSent(true); setForm({ name: "", email: "", phone: "", message: "" }); }
    catch { setError("Erro ao enviar mensagem. Tente novamente."); }
    finally { setSending(false); }
  };

  const handleChange = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  return (
    <section id="contato" data-testid="contact-section" className="py-20 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-16">
          <span className="text-sm font-bold uppercase tracking-[0.2em] text-brand-teal mb-4 block">Entre em Contato</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-brand-navy mb-4" data-testid="contact-title">Pronto para o Próximo Nível?</h2>
          <p className="text-base lg:text-lg text-slate-600 max-w-2xl mx-auto">Preencha o formulário e descubra como a Duali Soluções pode ajudar sua empresa</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h3 className="text-2xl font-bold text-brand-navy mb-8">Vamos Conversar</h3>
            <div className="space-y-6 mb-10">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-brand-teal/10 rounded-xl flex items-center justify-center shrink-0"><Phone className="text-brand-teal" size={22} /></div>
                <div><p className="font-semibold text-brand-navy mb-1">Telefone</p><a href="tel:+5511998686751" className="text-slate-600 hover:text-brand-teal transition-colors" data-testid="contact-phone">(11) 99868-6751</a></div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-brand-teal/10 rounded-xl flex items-center justify-center shrink-0"><Mail className="text-brand-teal" size={22} /></div>
                <div><p className="font-semibold text-brand-navy mb-1">E-mail</p><a href="mailto:comercial@dualibeneficios.com.br" className="text-slate-600 hover:text-brand-teal transition-colors" data-testid="contact-email">comercial@dualibeneficios.com.br</a></div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-brand-teal/10 rounded-xl flex items-center justify-center shrink-0"><Globe2 className="text-brand-teal" size={22} /></div>
                <div><p className="font-semibold text-brand-navy mb-1">Atendimento Nacional</p></div>
              </div>
            </div>
            <div className="bg-brand-navy/5 rounded-2xl p-6">
              <p className="text-sm font-semibold text-brand-navy mb-1">CNPJ</p>
              <p className="text-sm text-slate-600" data-testid="contact-cnpj">25.433.372/0001-32</p>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            {sent ? (
              <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                className="h-full flex flex-col items-center justify-center text-center p-8 bg-brand-teal/5 rounded-2xl border border-brand-teal/20" data-testid="contact-success">
                <CheckCircle className="text-brand-teal mb-4" size={56} />
                <h3 className="text-2xl font-bold text-brand-navy mb-2">Mensagem Enviada!</h3>
                <p className="text-slate-600 mb-6">Obrigado pelo contato. Nossa equipe retornará em breve.</p>
                <button onClick={() => setSent(false)} className="text-brand-teal font-semibold hover:underline" data-testid="contact-send-another">Enviar outra mensagem</button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div><Label htmlFor="name" className="text-brand-navy font-semibold mb-2 block">Nome Completo</Label>
                  <Input id="name" value={form.name} onChange={handleChange("name")} placeholder="Seu nome completo" required data-testid="contact-input-name" className="w-full rounded-md border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-transparent" /></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div><Label htmlFor="email" className="text-brand-navy font-semibold mb-2 block">E-mail Corporativo</Label>
                    <Input id="email" type="email" value={form.email} onChange={handleChange("email")} placeholder="seu@empresa.com.br" required data-testid="contact-input-email" className="w-full rounded-md border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-transparent" /></div>
                  <div><Label htmlFor="phone" className="text-brand-navy font-semibold mb-2 block">Telefone</Label>
                    <Input id="phone" value={form.phone} onChange={handleChange("phone")} placeholder="(11) 99999-9999" data-testid="contact-input-phone" className="w-full rounded-md border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-transparent" /></div>
                </div>
                <div><Label htmlFor="message" className="text-brand-navy font-semibold mb-2 block">Mensagem</Label>
                  <Textarea id="message" value={form.message} onChange={handleChange("message")} placeholder="Conte-nos sobre seu desafio ou necessidade..." rows={5} required data-testid="contact-input-message" className="w-full rounded-md border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-transparent resize-none" /></div>
                {error && <p className="text-red-500 text-sm" data-testid="contact-error">{error}</p>}
                <button type="submit" disabled={sending} data-testid="contact-submit-btn"
                  className="w-full bg-brand-teal text-white px-8 py-4 rounded-md font-semibold text-base hover:bg-teal-600 transition-all duration-300 hover:scale-[1.02] hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-3">
                  {sending ? "Enviando..." : "Enviar Mensagem"}<Send size={18} />
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
